import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { EstimateWithItems, estimatesApi, EstimateItem, EstimateStructure, CreateEstimateItemRequest } from '../../../api/estimatesApi';
import BottomSheet from '../../../shared/ui/forms/BottomSheet';
import ActionsMenuBar, { ActionItem } from '../../../shared/ui/ActionsMenuBar';
import WorkTypeSelector from '../../../shared/ui/forms/WorkTypeSelector';
import AutocompleteInput, { AutocompleteSuggestion } from '../../../shared/ui/forms/AutocompleteInput';
import './EstimateDetailModal.css';

interface EstimateDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  siteId: number;
  estimateId: number;
}

// Список единиц измерения
const UNIT_OPTIONS = [
  { value: 'шт', label: 'шт' },
  { value: 'м²', label: 'м²' },
  { value: 'м³', label: 'м³' },
  { value: 'п.м.', label: 'п.м.' },
  { value: 'час', label: 'час' },
  { value: 'изделие', label: 'изделие' },
  { value: 'комплекс', label: 'комплекс' }
];

const EstimateDetailModal: React.FC<EstimateDetailModalProps> = ({
  isOpen,
  onClose,
  siteId,
  estimateId
}) => {
  
  const [estimate, setEstimate] = useState<EstimateWithItems | null>(null);
  const [structure, setStructure] = useState<EstimateStructure[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingStructure, setLoadingStructure] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
  
  // Состояния для редактирования
  const [editingSectionId, setEditingSectionId] = useState<number | null>(null);
  const [editingItemId, setEditingItemId] = useState<number | null>(null);
  const [editSectionName, setEditSectionName] = useState('');
  
  // Состояния для редактирования элемента (все поля)
  const [editingItemData, setEditingItemData] = useState<{
    id: number;
    name: string;
    unit: string;
    quantity: number | undefined;
    pricePerUnit: number | undefined;
    total: number | undefined;
    typeOfWorkId: number | null;
  } | null>(null);

  // ОПТИМИЗАЦИЯ: Упростили состояния свайпа - один объект вместо трёх Map
  // Состояния для свайпа и выделения секций
  const [selectedSections, setSelectedSections] = useState<Set<number>>(new Set());
  const [showActionsMenuBar, setShowActionsMenuBar] = useState(false);
  const [sectionSwipeStates, setSectionSwipeStates] = useState<Map<number, {
    offset: number;
    isSwiping: boolean;
    wasSwiped: boolean;
  }>>(new Map());
  const sectionSwipeStartX = useRef<Map<number, number>>(new Map());
  const sectionSwipeStartY = useRef<Map<number, number>>(new Map());
  const sectionHeaderRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  // ОПТИМИЗАЦИЯ: Упростили состояния свайпа элементов - один объект вместо трёх Map
  // Состояния для свайпа и выделения элементов
  const [selectedItems, setSelectedItems] = useState<Set<number>>(new Set());
  const [showItemActionsMenuBar, setShowItemActionsMenuBar] = useState(false);
  const [itemSwipeStates, setItemSwipeStates] = useState<Map<number, {
    offset: number;
    isSwiping: boolean;
    wasSwiped: boolean;
  }>>(new Map());
  const itemSwipeStartX = useRef<Map<number, number>>(new Map());
  const itemSwipeStartY = useRef<Map<number, number>>(new Map());
  const itemRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const nameInputRefs = useRef<Map<number, HTMLInputElement>>(new Map());
  const [newlyCreatedItemId, setNewlyCreatedItemId] = useState<number | null>(null);
  const hasCollapsedOnOpen = useRef<boolean>(false); // Флаг для отслеживания сворачивания при открытии

  // Загрузка данных сметы
  useEffect(() => {
    if (isOpen && estimateId) {
      // Сбрасываем состояние при открытии
      setEstimate(null);
      setStructure([]);
      setError(null);
      setLoading(true);
      setLoadingStructure(true);
      setCollapsedSections(new Set()); // Сбрасываем свернутые секции при открытии
      hasCollapsedOnOpen.current = false; // Сбрасываем флаг сворачивания
      loadEstimateDetails();
      loadEstimateStructure();
    } else if (!isOpen) {
      // Сбрасываем состояние при закрытии
      setEstimate(null);
      setStructure([]);
      setError(null);
      setLoading(false);
      setLoadingStructure(false);
      setCollapsedSections(new Set());
      setSelectedSections(new Set());
      setShowActionsMenuBar(false);
      setSectionSwipeStates(new Map());
      setSelectedItems(new Set());
      setShowItemActionsMenuBar(false);
      setItemSwipeStates(new Map());
      setNewlyCreatedItemId(null);
      setNewlyCreatedSectionId(null);
      nameInputRefs.current.clear();
      hasCollapsedOnOpen.current = false;
    }
  }, [isOpen, estimateId, siteId]);

  const loadEstimateDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await estimatesApi.getEstimate(siteId, estimateId);
      
      // Backend returns { Estimate: ..., Items: [...] }, we need to transform it
      const transformedData: EstimateWithItems = {
        ...(data as any).Estimate || data,
        items: (data as any).Items || data.items || []
      };
      
      setEstimate(transformedData);
      setError(null);
    } catch (err: any) {
      console.error('Error loading estimate details:', err);
      
      // Если получили 403, значит нет прав - закрываем модальное окно
      if (err?.response?.status === 403) {
        setError('Нет прав для просмотра данной информации');
        // Закрываем модальное окно через небольшую задержку, чтобы пользователь увидел сообщение
        setTimeout(() => {
          onClose();
        }, 2000);
        return;
      }
      
      // Устанавливаем пустую смету, чтобы можно было работать со структурой
      setEstimate({
        id: estimateId,
        siteId: siteId,
        createdBy: 0,
        name: '',
        description: '',
        dateCreated: '',
        isClosed: false,
        items: []
      });
      setError('Не удалось загрузить данные сметы, но можно работать со структурой');
    } finally {
      setLoading(false);
    }
  };

  const loadEstimateStructure = async () => {
    try {
      setLoadingStructure(true);
      const structureData = await estimatesApi.getEstimateStructure(siteId, estimateId);
      setStructure(structureData);
    } catch (err) {
      console.error('Error loading estimate structure:', err);
      setStructure([]);
    } finally {
      setLoadingStructure(false);
    }
  };

  // ОПТИМИЗАЦИЯ: При загрузке структуры сворачиваем все секции по умолчанию
  useEffect(() => {
    if (structure.length > 0 && !loadingStructure && !hasCollapsedOnOpen.current) {
      // Рекурсивно собираем все ID секций
      const getAllSectionIds = (sections: EstimateStructure[]): Set<string> => {
        const ids = new Set<string>();
        sections.forEach(section => {
          ids.add(section.id.toString());
          if (section.children && section.children.length > 0) {
            getAllSectionIds(section.children).forEach(id => ids.add(id));
          }
        });
        return ids;
      };
      
      const allSectionIds = getAllSectionIds(structure);
      
      // Сворачиваем все секции при первой загрузке
      setCollapsedSections(allSectionIds);
      hasCollapsedOnOpen.current = true; // Помечаем, что сворачивание выполнено
    }
  }, [structure, loadingStructure]);

  // Создаем карту секций для быстрого доступа
  const buildSectionMap = (structure: EstimateStructure[]): Map<number, EstimateStructure> => {
    const map = new Map<number, EstimateStructure>();
    const flattenStructure = (nodes: EstimateStructure[]) => {
      nodes.forEach(node => {
        map.set(node.id, node);
        if (node.children && node.children.length > 0) {
          flattenStructure(node.children);
        }
      });
    };
    flattenStructure(structure);
    return map;
  };

  // Группировка элементов по секциям (рекурсивная структура)
  const groupItemsByStructure = (items: EstimateItem[], structure: EstimateStructure[]) => {
    const sectionMap = buildSectionMap(structure);
    const sectionItems: Map<number, EstimateItem[]> = new Map();
    
    // Инициализируем все секции
    sectionMap.forEach((section) => {
      sectionItems.set(section.id, []);
    });
    
    // Распределяем элементы по секциям
    items.forEach(item => {
      if (item.sectionId && sectionItems.has(item.sectionId)) {
        sectionItems.get(item.sectionId)!.push(item);
      }
    });

    return { structure, sectionItems };
  };

  // ОПТИМИЗАЦИЯ: Мемоизированная группировка элементов
  const { structure: treeStructure, sectionItems } = useMemo(() => {
    return groupItemsByStructure(estimate?.items || [], structure);
  }, [estimate?.items, structure]);

  // ОПТИМИЗАЦИЯ: Мемоизированный кеш сумм секций
  const sectionTotalsCache = useMemo(() => {
    const cache = new Map<number, number>();
    
    // Рекурсивная функция для вычисления суммы секции (включая дочерние секции)
    const calculateSectionTotal = (sectionId: number): number => {
      // Проверяем кэш
      if (cache.has(sectionId)) {
        return cache.get(sectionId)!;
      }
      
      // Находим секцию в исходной структуре (с children)
      const findSectionInStructure = (sections: EstimateStructure[]): EstimateStructure | null => {
        for (const sec of sections) {
          if (sec.id === sectionId) return sec;
          if (sec.children) {
            const found = findSectionInStructure(sec.children);
            if (found) return found;
          }
        }
        return null;
      };
      
      const section = findSectionInStructure(structure);
      if (!section) {
        cache.set(sectionId, 0);
        return 0;
      }
      
      // Сумма прямых элементов секции
      const directItems = sectionItems.get(sectionId) || [];
      const directTotal = directItems
        .filter(item => !item.isTotalRow)
        .reduce((sum, item) => sum + (item.total || 0), 0);
      
      // Сумма дочерних секций (рекурсивно)
      let childrenTotal = 0;
      if (section.children && section.children.length > 0) {
        childrenTotal = section.children.reduce((sum, child) => {
          const childTotal = calculateSectionTotal(child.id);
          return sum + childTotal;
        }, 0);
      }
      
      const total = directTotal + childrenTotal;
      cache.set(sectionId, total);
      return total;
    };
    
    // Вычисляем все суммы сразу
    const calculateAll = (sections: EstimateStructure[]) => {
      sections.forEach(section => {
        calculateSectionTotal(section.id);
        if (section.children) {
          calculateAll(section.children);
        }
      });
    };
    calculateAll(structure);
    
    return cache;
  }, [structure, sectionItems]);

  // Функция для получения суммы секции из кеша
  const calculateSectionTotal = useCallback((sectionId: number): number => {
    return sectionTotalsCache.get(sectionId) || 0;
  }, [sectionTotalsCache]);

  // Состояние для отслеживания новых созданных секций
  const [newlyCreatedSectionId, setNewlyCreatedSectionId] = useState<number | null>(null);

  // Создание корневой секции
  const handleCreateSection = async () => {
    try {
      await estimatesApi.createSection(siteId, estimateId, {
        name: 'Новая секция',
        sortOrder: structure.length
      });
      const updatedStructure = await estimatesApi.getEstimateStructure(siteId, estimateId);
      setStructure(updatedStructure);
      // Находим созданную секцию и переводим в режим редактирования
      const findSection = (nodes: EstimateStructure[]): EstimateStructure | null => {
        for (const node of nodes) {
          if (!node.parentId && node.name === 'Новая секция') {
            return node;
          }
          if (node.children) {
            const found = findSection(node.children);
            if (found) return found;
          }
        }
        return null;
      };
      const createdSection = findSection(updatedStructure);
      if (createdSection) {
        // Сворачиваем новую секцию по умолчанию
        setCollapsedSections(prev => {
          const newSet = new Set(prev);
          newSet.add(createdSection.id.toString());
          return newSet;
        });
        setEditingSectionId(createdSection.id);
        setEditSectionName('Новая секция');
        setNewlyCreatedSectionId(createdSection.id); // Помечаем как новую секцию
      }
    } catch (err) {
      console.error('Error creating section:', err);
      alert('Не удалось создать секцию');
    }
  };

  // Создание дочерней секции
  const handleCreateChildSection = async (parentSectionId: number) => {
    try {
      await estimatesApi.createChildSection(siteId, estimateId, parentSectionId, {
        name: 'Новая подсекция',
        parentId: parentSectionId,
        sortOrder: 0
      });
      const updatedStructure = await estimatesApi.getEstimateStructure(siteId, estimateId);
      setStructure(updatedStructure);
      // Находим созданную секцию и переводим в режим редактирования
      const findSection = (nodes: EstimateStructure[]): EstimateStructure | null => {
        for (const node of nodes) {
          if (node.parentId === parentSectionId && node.name === 'Новая подсекция') {
            return node;
          }
          if (node.children) {
            const found = findSection(node.children);
            if (found) return found;
          }
        }
        return null;
      };
      const createdSection = findSection(updatedStructure);
      if (createdSection) {
        // Сворачиваем новую секцию по умолчанию
        setCollapsedSections(prev => {
          const newSet = new Set(prev);
          newSet.add(createdSection.id.toString());
          return newSet;
        });
        setEditingSectionId(createdSection.id);
        setEditSectionName('Новая подсекция');
        setNewlyCreatedSectionId(createdSection.id); // Помечаем как новую секцию
      }
    } catch (err) {
      console.error('Error creating child section:', err);
      alert('Не удалось создать подсекцию');
    }
  };

  // Создание пустого элемента (inline редактирование)
  const handleCreateItem = async (sectionId: number) => {
    try {
      const itemToCreate: CreateEstimateItemRequest = {
        name: 'Новый элемент',
        unit: '',
        quantity: undefined,
        pricePerUnit: undefined,
        total: undefined,
        sectionId: sectionId,
        sortOrder: estimate?.items?.length || 0,
        isTotalRow: false
      };
      
      await estimatesApi.addEstimateItem(siteId, estimateId, itemToCreate);
      const updatedData = await estimatesApi.getEstimate(siteId, estimateId);
      const transformedData: EstimateWithItems = {
        ...(updatedData as any).Estimate || updatedData,
        items: (updatedData as any).Items || (updatedData as any).items || []
      };
      setEstimate(transformedData);
      
      // Находим созданный элемент и переводим в режим редактирования
      const newItem = transformedData.items.find((item: EstimateItem) => 
        item.sectionId === sectionId &&
        item.name === 'Новый элемент'
      );
      
      if (newItem) {
        setEditingItemData({
          id: newItem.id,
          name: newItem.name,
          unit: newItem.unit || '',
          quantity: newItem.quantity,
          pricePerUnit: newItem.pricePerUnit,
          total: newItem.total,
          typeOfWorkId: newItem.typeOfWorkId || null
        });
        setEditingItemId(newItem.id);
        // Помечаем элемент как только что созданный для автоматического выделения текста
        setNewlyCreatedItemId(newItem.id);
      }
    } catch (err) {
      console.error('Error creating item:', err);
      alert('Не удалось создать элемент');
    }
  };

  // Переключение состояния сворачивания секции
  const toggleSection = (sectionId: number) => {
    setCollapsedSections(prev => {
      const newSet = new Set(prev);
      const key = sectionId.toString();
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  // Начало редактирования секции
  const handleStartEditSection = (sectionId: number, currentName: string) => {
    setEditingSectionId(sectionId);
    setEditSectionName(currentName);
    setNewlyCreatedSectionId(null); // Сбрасываем флаг новой секции при редактировании существующей
  };

  // Сохранение секции
  const handleSaveSection = async (sectionId: number) => {
    if (!editSectionName.trim()) return;
    
    try {
      const findSection = (nodes: EstimateStructure[]): EstimateStructure | null => {
        for (const node of nodes) {
          if (node.id === sectionId) {
            return node;
          }
          if (node.children) {
            const found = findSection(node.children);
            if (found) return found;
          }
        }
        return null;
      };
      const section = findSection(structure);
      if (!section) return;
      
      await estimatesApi.updateEstimateStructure(siteId, estimateId, sectionId, {
        name: editSectionName.trim(),
        parentId: section.parentId,
        sortOrder: section.sortOrder
      });
      
      await loadEstimateStructure();
      setEditingSectionId(null);
      setEditSectionName('');
      setNewlyCreatedSectionId(null); // Сбрасываем флаг новой секции
    } catch (err) {
      console.error('Error updating section:', err);
      alert('Не удалось обновить секцию');
    }
  };

  // Отмена редактирования секции
  const handleCancelEditSection = async () => {
    // Если это новая созданная секция, удаляем её
    if (editingSectionId === newlyCreatedSectionId && newlyCreatedSectionId !== null) {
      try {
        await estimatesApi.deleteEstimateStructure(siteId, estimateId, newlyCreatedSectionId);
        await loadEstimateStructure();
      } catch (err) {
        console.error('Error deleting new section:', err);
        alert('Не удалось удалить секцию');
      }
    }
    
    setEditingSectionId(null);
    setEditSectionName('');
    setNewlyCreatedSectionId(null);
  };


  // Начало редактирования элемента (все поля)
  const handleStartEditItem = (item: EstimateItem) => {
    setEditingItemId(item.id);
    setEditingItemData({
      id: item.id,
      name: item.name,
      unit: item.unit || '',
      quantity: item.quantity,
      pricePerUnit: item.pricePerUnit,
      total: item.total,
      typeOfWorkId: item.typeOfWorkId || null
    });
  };

  // Сохранение элемента (все поля)
  const handleSaveItem = async (itemId: number) => {
    if (!editingItemData) return;
    
    try {
      const item = estimate?.items?.find(i => i.id === itemId);
      if (!item) return;
      
      // Вычисляем итого если изменилось количество или цена
      const calculatedTotal = editingItemData.quantity && editingItemData.pricePerUnit
        ? editingItemData.quantity * editingItemData.pricePerUnit
        : editingItemData.total;
      
      await estimatesApi.updateEstimateItem(siteId, estimateId, itemId, {
        name: editingItemData.name.trim() || 'Элемент',
        unit: editingItemData.unit || undefined,
        quantity: editingItemData.quantity,
        pricePerUnit: editingItemData.pricePerUnit,
        total: calculatedTotal,
        sectionId: item.sectionId,
        sortOrder: item.sortOrder,
        isTotalRow: item.isTotalRow,
        typeOfWorkId: editingItemData.typeOfWorkId || undefined
      });
      
      await loadEstimateDetails();
      setEditingItemId(null);
      setEditingItemData(null);
    } catch (err) {
      console.error('Error updating item:', err);
      alert('Не удалось обновить элемент');
    }
  };

  // Отмена редактирования элемента
  const handleCancelEditItem = async () => {
    if (!editingItemId) {
      setEditingItemId(null);
      setEditingItemData(null);
      return;
    }

    // Проверяем, является ли элемент только что созданным и пустым (по исходному состоянию из базы)
    const item = estimate?.items?.find(i => i.id === editingItemId);
    
    // Элемент считается новым и пустым, если:
    // - имя равно "Новый элемент"
    // - все поля пустые (unit, quantity, pricePerUnit, total, typeOfWorkId)
    const isNewEmptyItem = item && 
      item.name === 'Новый элемент' && 
      (!item.unit || item.unit.trim() === '') &&
      (!item.quantity || item.quantity === 0) &&
      (!item.pricePerUnit || item.pricePerUnit === 0) &&
      (!item.total || item.total === 0) &&
      (!item.typeOfWorkId);

    // Если элемент новый и пустой, удаляем его
    if (isNewEmptyItem && item) {
      try {
        await estimatesApi.deleteEstimateItem(siteId, estimateId, item.id);
        await loadEstimateDetails();
        setEditingItemId(null);
        setEditingItemData(null);
        return;
      } catch (err) {
        console.error('Error deleting empty item:', err);
        // Если не удалось удалить, просто закрываем редактирование
      }
    }

    // Если элемент не новый или не пустой, просто закрываем редактирование
    setEditingItemId(null);
    setEditingItemData(null);
  };

  // Удаление секции
  const handleDeleteSection = async (sectionId: number) => {
    const findSection = (nodes: EstimateStructure[]): EstimateStructure | null => {
      for (const node of nodes) {
        if (node.id === sectionId) {
          return node;
        }
        if (node.children) {
          const found = findSection(node.children);
          if (found) return found;
        }
      }
      return null;
    };
    const section = findSection(structure);
    if (!section) return;
    
    if (!window.confirm(`Удалить секцию "${section.name}"? Все дочерние секции и элементы внутри будут также удалены.`)) {
      return;
    }
    
    try {
      await estimatesApi.deleteEstimateStructure(siteId, estimateId, sectionId);
      await loadEstimateStructure();
      await loadEstimateDetails();
    } catch (err) {
      console.error('Error deleting section:', err);
      alert('Не удалось удалить секцию');
    }
  };


  // Удаление элемента
  const handleDeleteItem = async (itemId: number, itemName: string) => {
    if (!window.confirm(`Удалить элемент "${itemName}"?`)) {
      return;
    }
    
    try {
      await estimatesApi.deleteEstimateItem(siteId, estimateId, itemId);
      await loadEstimateDetails();
      // Если элемент был в режиме редактирования, выходим из него
      if (editingItemId === itemId) {
        handleCancelEditItem();
      }
    } catch (err) {
      console.error('Error deleting item:', err);
      alert('Не удалось удалить элемент');
    }
  };

  // Обновление данных редактируемого элемента
  const updateEditingItemData = (field: string, value: any) => {
    setEditingItemData(prev => {
      if (!prev) return prev;
      
      const updated = { ...prev, [field]: value };
      
      // Автоматически вычисляем итого при изменении количества или цены
      if (field === 'quantity' || field === 'pricePerUnit') {
        updated.total = updated.quantity && updated.pricePerUnit
          ? updated.quantity * updated.pricePerUnit
          : updated.total;
      }
      
      return updated;
    });
  };

  // Проверка, свернута ли секция
  const isSectionCollapsed = (sectionId: number) => {
    return collapsedSections.has(sectionId.toString());
  };

  // Обработка выделения секции
  const handleSectionSelect = useCallback((section: EstimateStructure) => {
    setSelectedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(section.id)) {
        newSet.delete(section.id);
      } else {
        newSet.add(section.id);
      }
      // Показываем меню только если есть выделенные секции
      if (newSet.size > 0) {
        setShowActionsMenuBar(true);
      } else {
        setShowActionsMenuBar(false);
      }
      return newSet;
    });
  }, []);

  // ОПТИМИЗАЦИЯ: Обработка начала свайпа для секции (упрощённый формат)
  const handleSectionSwipeStart = useCallback((sectionId: number, clientX: number, clientY: number) => {
    sectionSwipeStartX.current.set(sectionId, clientX);
    sectionSwipeStartY.current.set(sectionId, clientY);
    setSectionSwipeStates(prev => {
      const newMap = new Map(prev);
      const current = newMap.get(sectionId) || { offset: 0, isSwiping: false, wasSwiped: false };
      newMap.set(sectionId, { ...current, isSwiping: true });
      return newMap;
    });
  }, []);

  // ОПТИМИЗАЦИЯ: Обработка движения свайпа для секции (упрощённый формат)
  const handleSectionSwipeMove = useCallback((sectionId: number, clientX: number, clientY: number) => {
    setSectionSwipeStates(prev => {
      const current = prev.get(sectionId);
      if (!current || !current.isSwiping) return prev;

      const startX = sectionSwipeStartX.current.get(sectionId) || 0;
      const startY = sectionSwipeStartY.current.get(sectionId) || 0;
      const deltaX = clientX - startX;
      const deltaY = Math.abs(clientY - startY);

      // Игнорируем вертикальные свайпы
      if (deltaY > 10 && Math.abs(deltaX) < 10) {
        const newMap = new Map(prev);
        newMap.set(sectionId, { ...current, isSwiping: false });
        return newMap;
      }

      // Вычисляем новый offset
      let newOffset = current.offset;
      if (deltaX < 0) {
        newOffset = Math.max(deltaX, -80); // Максимум 80px влево
      } else if (current.offset < 0) {
        newOffset = Math.min(deltaX + current.offset, 0);
      }

      // Обновляем только если offset изменился
      if (newOffset !== current.offset) {
        const newMap = new Map(prev);
        newMap.set(sectionId, { ...current, offset: newOffset });
        return newMap;
      }
      
      return prev;
    });
  }, []);

  // ОПТИМИЗАЦИЯ: Обработка окончания свайпа для секции (упрощённый формат)
  const handleSectionSwipeEnd = useCallback((sectionId: number, section: EstimateStructure) => {
    setSectionSwipeStates(prev => {
      const current = prev.get(sectionId);
      if (!current || !current.isSwiping) return prev;

      const newMap = new Map(prev);
      
      // Если свайпнули достаточно далеко, выделяем/снимаем выделение
      if (current.offset < -60) {
        // Помечаем что был свайп
        newMap.set(sectionId, { offset: 0, isSwiping: false, wasSwiped: true });

        // Мгновенно возвращаем в исходное положение
        const headerRef = sectionHeaderRefs.current.get(sectionId);
        if (headerRef) {
          headerRef.style.transition = 'none';
          headerRef.style.transform = 'translateX(0px)';
          void headerRef.offsetHeight; // force reflow
        }

        // Вызываем выделение асинхронно
        setTimeout(() => {
          handleSectionSelect(section);
          // Восстанавливаем transition после небольшой задержки
          setTimeout(() => {
            if (headerRef) {
              headerRef.style.transition = '';
            }
            // Сбрасываем флаг свайпа после небольшой задержки
            setTimeout(() => {
              setSectionSwipeStates(prevState => {
                const updMap = new Map(prevState);
                const updCurrent = updMap.get(sectionId);
                if (updCurrent) {
                  updMap.set(sectionId, { ...updCurrent, wasSwiped: false });
                }
                return updMap;
              });
            }, 100);
          }, 10);
        }, 0);
      } else {
        // Возвращаем в исходное положение с анимацией
        newMap.set(sectionId, { offset: 0, isSwiping: false, wasSwiped: false });
      }
      
      return newMap;
    });
  }, [handleSectionSelect]);

  // Обработчики событий для свайпа (touch)
  const handleSectionTouchStart = useCallback((e: React.TouchEvent, sectionId: number) => {
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('a') || target.closest('input')) {
      return;
    }
    handleSectionSwipeStart(sectionId, e.touches[0].clientX, e.touches[0].clientY);
  }, [handleSectionSwipeStart]);

  const handleSectionTouchMove = useCallback((e: React.TouchEvent, sectionId: number) => {
    handleSectionSwipeMove(sectionId, e.touches[0].clientX, e.touches[0].clientY);
  }, [handleSectionSwipeMove]);

  const handleSectionTouchEnd = useCallback((e: React.TouchEvent, sectionId: number, section: EstimateStructure) => {
    handleSectionSwipeEnd(sectionId, section);
  }, [handleSectionSwipeEnd]);

  // Функция для поиска секции по ID
  const findSectionById = useCallback((sectionId: number): EstimateStructure | null => {
    const findInStructure = (nodes: EstimateStructure[]): EstimateStructure | null => {
      for (const node of nodes) {
        if (node.id === sectionId) {
          return node;
        }
        if (node.children && node.children.length > 0) {
          const found = findInStructure(node.children);
          if (found) return found;
        }
      }
      return null;
    };
    return findInStructure(structure);
  }, [structure]);

  // Обработчики событий для свайпа (mouse)
  const handleSectionMouseDown = useCallback((e: React.MouseEvent, sectionId: number) => {
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('a') || target.closest('input')) {
      return;
    }
    handleSectionSwipeStart(sectionId, e.clientX, e.clientY);
  }, [handleSectionSwipeStart]);

  // ОПТИМИЗАЦИЯ: Глобальные обработчики мыши для свайпа (упрощённый формат)
  useEffect(() => {
    const activeSwipingSections = Array.from(sectionSwipeStates.entries())
      .filter(([_, state]) => state.isSwiping)
      .map(([sectionId]) => sectionId);

    if (activeSwipingSections.length === 0) return;

    const handleMouseMove = (e: MouseEvent) => {
      activeSwipingSections.forEach(sectionId => {
        handleSectionSwipeMove(sectionId, e.clientX, e.clientY);
      });
    };

    const handleMouseUp = () => {
      activeSwipingSections.forEach(sectionId => {
        const section = findSectionById(sectionId);
        if (section) {
          handleSectionSwipeEnd(sectionId, section);
        }
      });
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [sectionSwipeStates, handleSectionSwipeMove, handleSectionSwipeEnd, findSectionById]);

  // Подготовка действий для меню
  const sectionActions: ActionItem[] = useMemo(() => {
    if (selectedSections.size === 0) return [];

    const selectedSectionsList = Array.from(selectedSections)
      .map(id => findSectionById(id))
      .filter((section): section is EstimateStructure => section !== null);

    if (selectedSectionsList.length === 0) return [];

    const isSingle = selectedSections.size === 1;
    const singleSection = isSingle ? selectedSectionsList[0] : null;

    const actions: ActionItem[] = [];

    // Редактирование (только для одной секции)
    if (isSingle && singleSection) {
      actions.push({
        id: 'edit',
        label: 'Редактировать',
        icon: 'ri-edit-line',
        onClick: () => {
          handleStartEditSection(singleSection.id, singleSection.name);
          setShowActionsMenuBar(false);
          setSelectedSections(new Set());
        },
        variant: 'primary'
      });
    }

    // Удаление
    actions.push({
      id: 'delete',
      label: isSingle ? 'Удалить' : `Удалить (${selectedSections.size})`,
      icon: 'ri-delete-bin-line',
      onClick: () => {
        selectedSectionsList.forEach(section => {
          handleDeleteSection(section.id);
        });
        setShowActionsMenuBar(false);
        setSelectedSections(new Set());
      },
      variant: 'danger'
    });

    return actions;
  }, [selectedSections, findSectionById, handleStartEditSection, handleCreateItem, handleCreateChildSection, handleDeleteSection]);

  // Обработка выделения элемента
  const handleItemSelect = useCallback((item: EstimateItem) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(item.id)) {
        newSet.delete(item.id);
      } else {
        newSet.add(item.id);
      }
      // Показываем меню только если есть выделенные элементы
      if (newSet.size > 0) {
        setShowItemActionsMenuBar(true);
      } else {
        setShowItemActionsMenuBar(false);
      }
      return newSet;
    });
  }, []);

  // ОПТИМИЗАЦИЯ: Обработка начала свайпа для элемента (упрощённый формат)
  const handleItemSwipeStart = useCallback((itemId: number, clientX: number, clientY: number) => {
    itemSwipeStartX.current.set(itemId, clientX);
    itemSwipeStartY.current.set(itemId, clientY);
    setItemSwipeStates(prev => {
      const newMap = new Map(prev);
      const current = newMap.get(itemId) || { offset: 0, isSwiping: false, wasSwiped: false };
      newMap.set(itemId, { ...current, isSwiping: true });
      return newMap;
    });
  }, []);

  // ОПТИМИЗАЦИЯ: Обработка движения свайпа для элемента (упрощённый формат)
  const handleItemSwipeMove = useCallback((itemId: number, clientX: number, clientY: number) => {
    setItemSwipeStates(prev => {
      const current = prev.get(itemId);
      if (!current || !current.isSwiping) return prev;

      const startX = itemSwipeStartX.current.get(itemId) || 0;
      const startY = itemSwipeStartY.current.get(itemId) || 0;
      const deltaX = clientX - startX;
      const deltaY = Math.abs(clientY - startY);

      // Игнорируем вертикальные свайпы
      if (deltaY > 10 && Math.abs(deltaX) < 10) {
        const newMap = new Map(prev);
        newMap.set(itemId, { ...current, isSwiping: false });
        return newMap;
      }

      // Вычисляем новый offset
      let newOffset = current.offset;
      if (deltaX < 0) {
        newOffset = Math.max(deltaX, -80); // Максимум 80px влево
      } else if (current.offset < 0) {
        newOffset = Math.min(deltaX + current.offset, 0);
      }

      // Обновляем только если offset изменился
      if (newOffset !== current.offset) {
        const newMap = new Map(prev);
        newMap.set(itemId, { ...current, offset: newOffset });
        return newMap;
      }
      
      return prev;
    });
  }, []);

  // ОПТИМИЗАЦИЯ: Обработка окончания свайпа для элемента (упрощённый формат)
  const handleItemSwipeEnd = useCallback((itemId: number, item: EstimateItem) => {
    setItemSwipeStates(prev => {
      const current = prev.get(itemId);
      if (!current || !current.isSwiping) return prev;

      const newMap = new Map(prev);
      
      // Если свайпнули достаточно далеко, выделяем/снимаем выделение
      if (current.offset < -60) {
        // Помечаем что был свайп
        newMap.set(itemId, { offset: 0, isSwiping: false, wasSwiped: true });

        // Мгновенно возвращаем в исходное положение
        const itemRef = itemRefs.current.get(itemId);
        if (itemRef) {
          itemRef.style.transition = 'none';
          itemRef.style.transform = 'translateX(0px)';
          void itemRef.offsetHeight; // force reflow
        }

        // Вызываем выделение асинхронно
        setTimeout(() => {
          handleItemSelect(item);
          // Восстанавливаем transition после небольшой задержки
          setTimeout(() => {
            if (itemRef) {
              itemRef.style.transition = '';
            }
            // Сбрасываем флаг свайпа после небольшой задержки
            setTimeout(() => {
              setItemSwipeStates(prevState => {
                const updMap = new Map(prevState);
                const updCurrent = updMap.get(itemId);
                if (updCurrent) {
                  updMap.set(itemId, { ...updCurrent, wasSwiped: false });
                }
                return updMap;
              });
            }, 100);
          }, 10);
        }, 0);
      } else {
        // Возвращаем в исходное положение с анимацией
        newMap.set(itemId, { offset: 0, isSwiping: false, wasSwiped: false });
      }
      
      return newMap;
    });
  }, [handleItemSelect]);

  // Обработчики событий для свайпа элементов (touch)
  const handleItemTouchStart = useCallback((e: React.TouchEvent, itemId: number) => {
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('a') || target.closest('input') || target.closest('select')) {
      return;
    }
    handleItemSwipeStart(itemId, e.touches[0].clientX, e.touches[0].clientY);
  }, [handleItemSwipeStart]);

  const handleItemTouchMove = useCallback((e: React.TouchEvent, itemId: number) => {
    handleItemSwipeMove(itemId, e.touches[0].clientX, e.touches[0].clientY);
  }, [handleItemSwipeMove]);

  const handleItemTouchEnd = useCallback((e: React.TouchEvent, itemId: number, item: EstimateItem) => {
    handleItemSwipeEnd(itemId, item);
  }, [handleItemSwipeEnd]);

  // Обработчики событий для свайпа элементов (mouse)
  const handleItemMouseDown = useCallback((e: React.MouseEvent, itemId: number) => {
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('a') || target.closest('input') || target.closest('select')) {
      return;
    }
    handleItemSwipeStart(itemId, e.clientX, e.clientY);
  }, [handleItemSwipeStart]);

  // ОПТИМИЗАЦИЯ: Глобальные обработчики мыши для свайпа элементов (упрощённый формат)
  useEffect(() => {
    const activeSwipingItems = Array.from(itemSwipeStates.entries())
      .filter(([_, state]) => state.isSwiping)
      .map(([itemId]) => itemId);

    if (activeSwipingItems.length === 0) return;

    const handleMouseMove = (e: MouseEvent) => {
      activeSwipingItems.forEach(itemId => {
        handleItemSwipeMove(itemId, e.clientX, e.clientY);
      });
    };

    const handleMouseUp = () => {
      activeSwipingItems.forEach(itemId => {
        const item = estimate?.items?.find(i => i.id === itemId);
        if (item) {
          handleItemSwipeEnd(itemId, item);
        }
      });
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [itemSwipeStates, handleItemSwipeMove, handleItemSwipeEnd, estimate]);

  // Подготовка действий для меню элементов
  const itemActions: ActionItem[] = useMemo(() => {
    if (selectedItems.size === 0) return [];

    const selectedItemsList = Array.from(selectedItems)
      .map(id => estimate?.items?.find(item => item.id === id))
      .filter((item): item is EstimateItem => item !== undefined);

    if (selectedItemsList.length === 0) return [];

    const isSingle = selectedItems.size === 1;
    const singleItem = isSingle ? selectedItemsList[0] : null;

    const actions: ActionItem[] = [];

    // Редактирование (только для одного элемента)
    if (isSingle && singleItem) {
      actions.push({
        id: 'edit',
        label: 'Редактировать',
        icon: 'ri-edit-line',
        onClick: () => {
          handleStartEditItem(singleItem);
          setShowItemActionsMenuBar(false);
          setSelectedItems(new Set());
        },
        variant: 'primary'
      });
    }

    // Удаление
    actions.push({
      id: 'delete',
      label: isSingle ? 'Удалить' : `Удалить (${selectedItems.size})`,
      icon: 'ri-delete-bin-line',
      onClick: () => {
        selectedItemsList.forEach(item => {
          handleDeleteItem(item.id, item.name);
        });
        setShowItemActionsMenuBar(false);
        setSelectedItems(new Set());
      },
      variant: 'danger'
    });

    return actions;
  }, [selectedItems, estimate, handleStartEditItem, handleDeleteItem]);

  // Форматирование чисел
  const formatNumber = (value?: number) => {
    if (value === undefined || value === null) return '—';
    return value.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Форматирование даты
  const formatDate = (dateString?: string) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Получение предложений для названий секций (собираем все уникальные названия)
  const getSectionNameSuggestions = useCallback((): string[] => {
    const names = new Set<string>();
    
    const collectNames = (sections: EstimateStructure[]) => {
      sections.forEach(section => {
        names.add(section.name);
        if (section.children && section.children.length > 0) {
          collectNames(section.children);
        }
      });
    };
    
    collectNames(structure);
    return Array.from(names).sort();
  }, [structure]);

  // Получение предложений для названий элементов на основе текущей секции
  const getItemNameSuggestions = useCallback((currentSectionId: number): AutocompleteSuggestion[] => {
    const suggestionsMap = new Map<string, AutocompleteSuggestion>();
    
    // Найти текущую секцию
    const findSection = (sections: EstimateStructure[], id: number): EstimateStructure | null => {
      for (const section of sections) {
        if (section.id === id) return section;
        if (section.children) {
          const found = findSection(section.children, id);
          if (found) return found;
        }
      }
      return null;
    };
    
    const currentSection = findSection(structure, currentSectionId);
    if (!currentSection) return [];
    
    const currentSectionName = currentSection.name;
    
    // Найти все секции с таким же названием
    const findSectionsByName = (sections: EstimateStructure[], name: string): number[] => {
      const ids: number[] = [];
      sections.forEach(section => {
        if (section.name === name) {
          ids.push(section.id);
        }
        if (section.children) {
          ids.push(...findSectionsByName(section.children, name));
        }
      });
      return ids;
    };
    
    const sectionIds = findSectionsByName(structure, currentSectionName);
    
    // Собрать элементы из этих секций
    estimate?.items?.forEach(item => {
      if (item.sectionId && sectionIds.includes(item.sectionId) && item.name !== 'Новый элемент') {
        // Используем Map чтобы хранить только первое вхождение каждого имени с данными
        if (!suggestionsMap.has(item.name)) {
          suggestionsMap.set(item.name, {
            name: item.name,
            pricePerUnit: item.pricePerUnit,
            unit: item.unit,
            typeOfWorkId: item.typeOfWorkId
          });
        }
      }
    });
    
    return Array.from(suggestionsMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [structure, estimate]);

  // Автоматический фокус для только что созданного элемента
  useEffect(() => {
    if (editingItemId && newlyCreatedItemId === editingItemId) {
      // Сбрасываем флаг после небольшой задержки (AutocompleteInput имеет autoFocus)
      setTimeout(() => {
        setNewlyCreatedItemId(null);
      }, 100);
    }
  }, [editingItemId, newlyCreatedItemId]);

  // Закрытие по Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        if (editingItemId) {
          handleCancelEditItem();
        } else {
          onClose();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose, editingItemId]);

  // Сохранение при клике вне редактируемого элемента (отключено для предотвращения закрытия при клике на поля)
  // Сохранение происходит только по кнопке "Сохранить", Enter или Escape
  // useEffect(() => {
  //   if (!editingItemId) return;
  //   
  //   const handleClickOutside = (e: MouseEvent) => {
  //     const target = e.target as HTMLElement;
  //     
  //     // Проверяем, был ли клик на кнопку (редактировать, удалить, сохранить)
  //     if (target.closest('button')) {
  //       return;
  //     }
  //     
  //     // Проверяем, был ли клик внутри редактируемого элемента (десктоп)
  //     const editingRow = document.querySelector('.estimate-table-row.editing');
  //     if (editingRow && editingRow.contains(target)) {
  //       return;
  //     }
  //     
  //     // Проверяем, был ли клик внутри редактируемого элемента (мобильная версия)
  //     const editingCard = document.querySelector('.estimate-card.editing');
  //     if (editingCard && editingCard.contains(target)) {
  //       return;
  //     }
  //     
  //     // Если клик вне редактируемого элемента, сохраняем
  //     handleSaveItem(editingItemId);
  //   };

  //   // Используем setTimeout чтобы обработчик не сработал сразу при открытии редактирования
  //   const timeoutId = setTimeout(() => {
  //     document.addEventListener('mousedown', handleClickOutside);
  //   }, 300);

  //   return () => {
  //     clearTimeout(timeoutId);
  //     document.removeEventListener('mousedown', handleClickOutside);
  //   };
  // }, [editingItemId]);

  const totalAmount = estimate?.items?.reduce((sum, item) => 
    sum + (item.isTotalRow ? 0 : (item.total || 0)), 0
  ) || 0;

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      size="fullscreen"
      showHandle={true}
      allowSwipeToClose={true}
      allowSwipeToExpand={true}
      className="estimate-detail-bottom-sheet"
    >
      <div className="estimate-detail-wrapper">
        {/* Информация */}
        <div className="estimate-detail-meta">
          <span>
            <i className="ri-calendar-line"></i>
            Создана: {formatDate(estimate?.dateCreated)}
          </span>
          {estimate?.dateUpdated && (
            <span>
              <i className="ri-refresh-line"></i>
              Обновлена: {formatDate(estimate.dateUpdated)}
            </span>
          )}
          {estimate && estimate.items && estimate.items.length > 0 && totalAmount > 0 && (
            <span className="estimate-total-amount">
              <i className="ri-money-dollar-circle-line"></i>
              {formatNumber(totalAmount)} ₽
            </span>
          )}
          <span className={`estimate-status ${estimate?.isClosed ? 'closed' : 'active'}`}>
            <i className={estimate?.isClosed ? 'ri-lock-line' : 'ri-unlock-line'}></i>
            {estimate?.isClosed ? 'Закрыта' : 'Активна'}
          </span>
        </div>

        <div className="estimate-detail-content">
          {loading && loadingStructure ? (
            <div className="estimate-detail-loading">
              <i className="ri-loader-4-line ri-spin"></i>
              <span>Загрузка сметы...</span>
            </div>
          ) : error && !estimate && structure.length === 0 && !loading && !loadingStructure ? (
            <div className="estimate-detail-error">
              <i className="ri-error-warning-line"></i>
              <span>{error}</span>
              <button onClick={loadEstimateDetails}>Повторить</button>
            </div>
          ) : structure.length === 0 && (!estimate?.items || estimate.items.length === 0) && !loading && !loadingStructure ? (
            <div className="estimate-detail-empty">
              <i className="ri-inbox-line"></i>
              <span>Смета пуста. Начните с создания секций (комнат)</span>
              <p className="empty-hint">Сначала создайте секции, затем добавьте элементы сметы</p>
              <div style={{ marginTop: '16px' }}>
                <button
                  className="btn-create-section-compact-empty"
                  onClick={handleCreateSection}
                  title="Создать первую секцию"
                >
                  <i className="ri-folder-add-line"></i>
                  <span>Создать секцию</span>
                </button>
              </div>
            </div>
          ) : (structure.length > 0 || estimate || (!loading && !loadingStructure)) ? (
            <>
              {/* Список элементов по структуре */}
              <div className="estimate-items">
                {(() => {
                  // Рекурсивный компонент для отображения секции
                  const renderSection = (section: EstimateStructure, level: number = 0): React.ReactNode => {
                    const sectionItemsList = sectionItems.get(section.id) || [];
                    const isCollapsed = isSectionCollapsed(section.id);
                    const sectionTotal = calculateSectionTotal(section.id);
                    
                    // ОПТИМИЗАЦИЯ: Используем упрощённый формат состояний свайпа
                    const swipeState = sectionSwipeStates.get(section.id) || { offset: 0, isSwiping: false, wasSwiped: false };
                    const swipeOffset = swipeState.offset;
                    const isSwiping = swipeState.isSwiping;
                    const wasSwiped = swipeState.wasSwiped;
                    const isSelected = selectedSections.has(section.id);
                    
                    return (
                      <div key={section.id} className={`estimate-section-tree estimate-section-level-${level} ${isSelected ? 'section-selected' : ''}`}>
                        <div className="estimate-section-header-wrapper">
                          {/* Иконка выделения под заголовком */}
                          <div 
                            className="estimate-section-swipe-action"
                            style={{
                              opacity: Math.min(Math.abs(swipeOffset) / 60, 1)
                            }}
                          >
                            <i 
                              className={`${isSelected ? "ri-checkbox-circle-fill" : "ri-checkbox-circle-line"} ${isSwiping && Math.abs(swipeOffset) > 10 ? 'swipe-icon-animating' : ''}`}
                            ></i>
                          </div>
                          
                          {/* Заголовок секции с свайпом */}
                          <div 
                            className="estimate-section-header-tree"
                            ref={(el) => {
                              if (el) {
                                sectionHeaderRefs.current.set(section.id, el);
                              } else {
                                sectionHeaderRefs.current.delete(section.id);
                              }
                            }}
                            style={{
                              transform: `translateX(${swipeOffset}px)`,
                              transition: (isSwiping) ? 'none' : undefined
                            }}
                            onTouchStart={(e) => handleSectionTouchStart(e, section.id)}
                            onTouchMove={(e) => handleSectionTouchMove(e, section.id)}
                            onTouchEnd={(e) => handleSectionTouchEnd(e, section.id, section)}
                            onMouseDown={(e) => handleSectionMouseDown(e, section.id)}
                          >
                            <div className="tree-indent" style={{ width: `${level * 16}px` }}>
                              {level > 0 && <div className="tree-line"></div>}
                            </div>
                            <div 
                              className={`estimate-section-title-tree ${isSelected ? 'section-title-selected' : ''}`}
                              onClick={(e) => {
                                // Предотвращаем раскрытие если был свайп
                                if (wasSwiped) {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setSectionSwipeStates(prev => {
                                    const newMap = new Map(prev);
                                    const current = newMap.get(section.id);
                                    if (current) {
                                      newMap.set(section.id, { ...current, wasSwiped: false });
                                    }
                                    return newMap;
                                  });
                                  return;
                                }
                                if (!editingSectionId) {
                                  toggleSection(section.id);
                                }
                              }}
                            >
                            <i className={`ri-arrow-${isCollapsed ? 'right' : 'down'}-s-line estimate-section-toggle`}></i>
                            <i className="ri-folder-line"></i>
                            {editingSectionId === section.id ? (
                              <div className="inline-edit-input" onClick={(e) => e.stopPropagation()}>
                                <AutocompleteInput
                                  value={editSectionName}
                                  onChange={setEditSectionName}
                                  suggestions={getSectionNameSuggestions()}
                                  placeholder="Название секции"
                                  autoFocus
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      handleSaveSection(section.id);
                                    } else if (e.key === 'Escape') {
                                      e.preventDefault();
                                      handleCancelEditSection();
                                    }
                                  }}
                                  className="edit-name-input"
                                />
                                <button
                                  className="btn-save-section"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSaveSection(section.id);
                                  }}
                                  title="Сохранить"
                                >
                                  <i className="ri-check-line"></i>
                                </button>
                                <button
                                  className="btn-cancel-section"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCancelEditSection();
                                  }}
                                  title="Отменить"
                                >
                                  <i className="ri-close-line"></i>
                                </button>
                              </div>
                            ) : (
                              <>
                                <span className="section-name-text">{section.name}</span>
                                {!editingSectionId && (
                                  <span className="estimate-section-summary">
                                    {sectionTotal > 0 ? `${formatNumber(sectionTotal)} ₽` : '—'}
                                  </span>
                                )}
                              </>
                            )}
                          </div>
                          </div>
                        </div>
                        
                        {!isCollapsed && (
                          <div className="estimate-section-content-tree">
                            {/* Отображаем элементы этой секции */}
                            {sectionItemsList.length > 0 && (
                              <>
                                {/* Desktop table view */}
                                <div className="estimate-table estimate-table-desktop">
                                  <div className="estimate-table-header">
                                    <div className="col-name">Наименование</div>
                                    <div className="col-unit">Ед. изм.</div>
                                    <div className="col-quantity">Кол-во</div>
                                    <div className="col-price">Цена</div>
                                    <div className="col-total">Итого</div>
                                    <div className="col-actions"></div>
                                  </div>
                                  
                                  <div className="estimate-table-body">
                                    {sectionItemsList.map((item) => {
                                      // ОПТИМИЗАЦИЯ: Используем упрощённый формат состояний свайпа
                                      const itemSwipeState = itemSwipeStates.get(item.id) || { offset: 0, isSwiping: false, wasSwiped: false };
                                      const itemSwipeOffset = itemSwipeState.offset;
                                      const itemIsSwiping = itemSwipeState.isSwiping;
                                      const itemWasSwipedFlag = itemSwipeState.wasSwiped;
                                      const itemIsSelected = selectedItems.has(item.id);
                                      
                                      return (
                                      <div 
                                        key={item.id}
                                        className="estimate-item-wrapper"
                                      >
                                        {/* Иконка выделения под элементом */}
                                        <div 
                                          className="estimate-item-swipe-action"
                                          style={{
                                            opacity: Math.min(Math.abs(itemSwipeOffset) / 60, 1)
                                          }}
                                        >
                                          <i 
                                            className={itemIsSelected ? "ri-checkbox-circle-fill" : "ri-checkbox-circle-line"}
                                            style={{
                                              animation: itemIsSwiping && Math.abs(itemSwipeOffset) > 10 ? 'pulseRotate 2s ease-in-out infinite' : 'none'
                                            }}
                                          ></i>
                                        </div>
                                        
                                        {/* Элемент с свайпом */}
                                        <div 
                                          key={item.id} 
                                          className={`estimate-table-row ${item.isTotalRow ? 'total-row' : ''} ${editingItemId === item.id ? 'editing' : ''} ${itemIsSelected ? 'item-selected' : ''}`}
                                          ref={(el) => {
                                            if (el) {
                                              itemRefs.current.set(item.id, el);
                                            } else {
                                              itemRefs.current.delete(item.id);
                                            }
                                          }}
                                          style={{
                                            transform: `translateX(${itemSwipeOffset}px)`,
                                            transition: (itemIsSwiping) ? 'none' : undefined
                                          }}
                                          onTouchStart={(e) => handleItemTouchStart(e, item.id)}
                                          onTouchMove={(e) => handleItemTouchMove(e, item.id)}
                                          onTouchEnd={(e) => handleItemTouchEnd(e, item.id, item)}
                                          onMouseDown={(e) => handleItemMouseDown(e, item.id)}
                                          onClick={(e) => {
                                            // Предотвращаем клик если был свайп
                                            if (itemWasSwipedFlag) {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              setItemSwipeStates(prev => {
                                                const newMap = new Map(prev);
                                                const current = newMap.get(item.id);
                                                if (current) {
                                                  newMap.set(item.id, { ...current, wasSwiped: false });
                                                }
                                                return newMap;
                                              });
                                              return;
                                            }
                                          }}
                                        >
                                        <div className="col-name" onClick={(e) => e.stopPropagation()}>
                                          {editingItemId === item.id ? (
                                            <div className="edit-name-wrapper">
                                              <AutocompleteInput
                                                value={editingItemData?.name || ''}
                                                onChange={(value) => updateEditingItemData('name', value)}
                                                suggestions={getItemNameSuggestions(item.sectionId || 0)}
                                                placeholder="Наименование работы"
                                                autoFocus
                                                onSuggestionSelect={(suggestion) => {
                                                  // Автоматически заполняем цену, единицу измерения и вид работы при выборе предложения
                                                  if (suggestion.pricePerUnit !== undefined) {
                                                    updateEditingItemData('pricePerUnit', suggestion.pricePerUnit);
                                                  }
                                                  if (suggestion.unit) {
                                                    updateEditingItemData('unit', suggestion.unit);
                                                  }
                                                  if (suggestion.typeOfWorkId !== undefined) {
                                                    updateEditingItemData('typeOfWorkId', suggestion.typeOfWorkId);
                                                  }
                                                }}
                                                onKeyDown={(e) => {
                                                  if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    handleSaveItem(item.id);
                                                  } else if (e.key === 'Escape') {
                                                    e.preventDefault();
                                                    handleCancelEditItem();
                                                  }
                                                }}
                                                className="edit-input"
                                              />
                                              <div className="edit-work-type-wrapper" onClick={(e) => e.stopPropagation()}>
                                                <WorkTypeSelector
                                                  value={editingItemData?.typeOfWorkId || null}
                                                  onChange={(typeId) => updateEditingItemData('typeOfWorkId', typeId)}
                                                  placeholder="Выберите вид работы"
                                                  compact={true}
                                                />
                                              </div>
                                            </div>
                                          ) : (
                                            <>
                                              <span className="item-name-text">{item.name}</span>
                                              {item.typeOfWork && (
                                                <span className="item-type-badge">{item.typeOfWork.name}</span>
                                              )}
                                            </>
                                          )}
                                        </div>
                                        <div className="col-unit" onClick={(e) => e.stopPropagation()}>
                                          {editingItemId === item.id ? (
                                            <select
                                              value={editingItemData?.unit || ''}
                                              onChange={(e) => updateEditingItemData('unit', e.target.value)}
                                              onClick={(e) => e.stopPropagation()}
                                              className="edit-input edit-select"
                                            >
                                              <option value="">—</option>
                                              {UNIT_OPTIONS.map(option => (
                                                <option key={option.value} value={option.value}>
                                                  {option.label}
                                                </option>
                                              ))}
                                            </select>
                                          ) : (
                                            item.unit || '—'
                                          )}
                                        </div>
                                        <div className="col-quantity" onClick={(e) => e.stopPropagation()}>
                                          {editingItemId === item.id ? (
                                            <input
                                              type="number"
                                              value={editingItemData?.quantity !== undefined && editingItemData?.quantity !== null ? editingItemData.quantity : ''}
                                              onChange={(e) => updateEditingItemData('quantity', e.target.value ? Number(e.target.value) : undefined)}
                                              onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                  e.preventDefault();
                                                  handleSaveItem(item.id);
                                                } else if (e.key === 'Escape') {
                                                  e.preventDefault();
                                                  handleCancelEditItem();
                                                }
                                              }}
                                              onClick={(e) => e.stopPropagation()}
                                              placeholder="0"
                                              step="0.01"
                                              className="edit-input"
                                            />
                                          ) : (
                                            item.quantity ? formatNumber(item.quantity) : '—'
                                          )}
                                        </div>
                                        <div className="col-price" onClick={(e) => e.stopPropagation()}>
                                          {editingItemId === item.id ? (
                                            <input
                                              type="number"
                                              value={editingItemData?.pricePerUnit !== undefined && editingItemData?.pricePerUnit !== null ? editingItemData.pricePerUnit : ''}
                                              onChange={(e) => updateEditingItemData('pricePerUnit', e.target.value ? Number(e.target.value) : undefined)}
                                              onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                  e.preventDefault();
                                                  handleSaveItem(item.id);
                                                } else if (e.key === 'Escape') {
                                                  e.preventDefault();
                                                  handleCancelEditItem();
                                                }
                                              }}
                                              onClick={(e) => e.stopPropagation()}
                                              placeholder="0"
                                              step="0.01"
                                              className="edit-input"
                                            />
                                          ) : (
                                            item.pricePerUnit ? formatNumber(item.pricePerUnit) : '—'
                                          )}
                                        </div>
                                        <div className="col-total" onClick={(e) => e.stopPropagation()}>
                                          {item.total ? (
                                            <span className="total-amount">{formatNumber(item.total)} ₽</span>
                                          ) : '—'}
                                        </div>
                                        <div className="col-actions">
                                          {editingItemId === item.id && (
                                            <>
                                              <button
                                                className="btn-save-item"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleSaveItem(item.id);
                                                }}
                                                title="Сохранить изменения"
                                              >
                                                <i className="ri-check-line"></i>
                                              </button>
                                              <button
                                                className="btn-cancel-item"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleCancelEditItem();
                                                }}
                                                title="Отменить редактирование"
                                              >
                                                <i className="ri-close-line"></i>
                                              </button>
                                            </>
                                          )}
                                        </div>
                                        </div>
                                      </div>
                                    );
                                    })}
                                  </div>
                                </div>

                                {/* Mobile card view */}
                                <div className="estimate-cards estimate-cards-mobile">
                                  {sectionItemsList.map((item) => {
                                    const quantityWithUnit = item.quantity && item.unit 
                                      ? `${formatNumber(item.quantity)} ${item.unit}`
                                      : item.quantity 
                                        ? formatNumber(item.quantity)
                                        : item.unit || '—';

                                    // ОПТИМИЗАЦИЯ: Используем упрощённый формат состояний свайпа
                                    const itemSwipeState = itemSwipeStates.get(item.id) || { offset: 0, isSwiping: false, wasSwiped: false };
                                    const itemSwipeOffset = itemSwipeState.offset;
                                    const itemIsSwiping = itemSwipeState.isSwiping;
                                    const itemWasSwipedFlag = itemSwipeState.wasSwiped;
                                    const itemIsSelected = selectedItems.has(item.id);

                                    return (
                                      <div 
                                        key={item.id}
                                        className="estimate-item-wrapper"
                                      >
                                        {/* Иконка выделения под элементом */}
                                        <div 
                                          className="estimate-item-swipe-action"
                                          style={{
                                            opacity: Math.min(Math.abs(itemSwipeOffset) / 60, 1)
                                          }}
                                        >
                                          <i 
                                            className={itemIsSelected ? "ri-checkbox-circle-fill" : "ri-checkbox-circle-line"}
                                            style={{
                                              animation: itemIsSwiping && Math.abs(itemSwipeOffset) > 10 ? 'pulseRotate 2s ease-in-out infinite' : 'none'
                                            }}
                                          ></i>
                                        </div>
                                        
                                        {/* Элемент с свайпом */}
                                        <div 
                                          key={item.id} 
                                          className={`estimate-card ${item.isTotalRow ? 'total-card' : ''} ${editingItemId === item.id ? 'editing' : ''} ${itemIsSelected ? 'item-selected' : ''}`}
                                          ref={(el) => {
                                            if (el) {
                                              itemRefs.current.set(item.id, el);
                                            } else {
                                              itemRefs.current.delete(item.id);
                                            }
                                          }}
                                          style={{
                                            transform: `translateX(${itemSwipeOffset}px)`,
                                            transition: (itemIsSwiping) ? 'none' : undefined
                                          }}
                                          onTouchStart={(e) => handleItemTouchStart(e, item.id)}
                                          onTouchMove={(e) => handleItemTouchMove(e, item.id)}
                                          onTouchEnd={(e) => handleItemTouchEnd(e, item.id, item)}
                                          onMouseDown={(e) => handleItemMouseDown(e, item.id)}
                                          onClick={(e) => {
                                            // Предотвращаем клик если был свайп
                                            if (itemWasSwipedFlag) {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              setItemSwipeStates(prev => {
                                                const newMap = new Map(prev);
                                                const current = newMap.get(item.id);
                                                if (current) {
                                                  newMap.set(item.id, { ...current, wasSwiped: false });
                                                }
                                                return newMap;
                                              });
                                              return;
                                            }
                                          }}
                                        >
                                        <div className="estimate-card-name">
                                          {editingItemId === item.id ? (
                                            <div className="inline-edit-input">
                                              <AutocompleteInput
                                                value={editingItemData?.name || ''}
                                                onChange={(value) => updateEditingItemData('name', value)}
                                                suggestions={getItemNameSuggestions(item.sectionId || 0)}
                                                placeholder="Наименование работы"
                                                autoFocus
                                                onSuggestionSelect={(suggestion) => {
                                                  // Автоматически заполняем цену, единицу измерения и вид работы при выборе предложения
                                                  if (suggestion.pricePerUnit !== undefined) {
                                                    updateEditingItemData('pricePerUnit', suggestion.pricePerUnit);
                                                  }
                                                  if (suggestion.unit) {
                                                    updateEditingItemData('unit', suggestion.unit);
                                                  }
                                                  if (suggestion.typeOfWorkId !== undefined) {
                                                    updateEditingItemData('typeOfWorkId', suggestion.typeOfWorkId);
                                                  }
                                                }}
                                                onKeyDown={(e) => {
                                                  if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    handleSaveItem(item.id);
                                                  } else if (e.key === 'Escape') {
                                                    e.preventDefault();
                                                    handleCancelEditItem();
                                                  }
                                                }}
                                                className="edit-name-input"
                                              />
                                              <div className="mobile-edit-work-type" onClick={(e) => e.stopPropagation()}>
                                                <WorkTypeSelector
                                                  value={editingItemData?.typeOfWorkId || null}
                                                  onChange={(typeId) => updateEditingItemData('typeOfWorkId', typeId)}
                                                  placeholder="Выберите вид работы"
                                                  compact={true}
                                                />
                                              </div>
                                            </div>
                                          ) : (
                                            <>
                                              <span className="item-name-text">{item.name}</span>
                                              {item.typeOfWork && (
                                                <span className="item-type-badge">{item.typeOfWork.name}</span>
                                              )}
                                            </>
                                          )}
                                        </div>
                                        <div className="estimate-card-info">
                                          <div className="estimate-card-quantity">
                                            {editingItemId === item.id ? (
                                              <div className="mobile-edit-inputs">
                                                <input
                                                  type="number"
                                                  value={editingItemData?.quantity !== undefined && editingItemData?.quantity !== null ? editingItemData.quantity : ''}
                                                  onChange={(e) => updateEditingItemData('quantity', e.target.value ? Number(e.target.value) : undefined)}
                                                  placeholder="Кол-во"
                                                  step="0.01"
                                                  className="edit-input-small"
                                                  onClick={(e) => e.stopPropagation()}
                                                />
                                                <select
                                                  value={editingItemData?.unit || ''}
                                                  onChange={(e) => updateEditingItemData('unit', e.target.value)}
                                                  onClick={(e) => e.stopPropagation()}
                                                  className="edit-input-small edit-select"
                                                >
                                                  <option value="">—</option>
                                                  {UNIT_OPTIONS.map(option => (
                                                    <option key={option.value} value={option.value}>
                                                      {option.label}
                                                    </option>
                                                  ))}
                                                </select>
                                              </div>
                                            ) : (
                                              quantityWithUnit
                                            )}
                                          </div>
                                          <div className="estimate-card-price">
                                            {editingItemId === item.id ? (
                                              <input
                                                type="number"
                                                value={editingItemData?.pricePerUnit !== undefined && editingItemData?.pricePerUnit !== null ? editingItemData.pricePerUnit : ''}
                                                onChange={(e) => updateEditingItemData('pricePerUnit', e.target.value ? Number(e.target.value) : undefined)}
                                                placeholder="Цена"
                                                step="0.01"
                                                className="edit-input-small"
                                                onClick={(e) => e.stopPropagation()}
                                              />
                                            ) : (
                                              item.pricePerUnit ? `${formatNumber(item.pricePerUnit)} ₽` : '—'
                                            )}
                                          </div>
                                          <div className="estimate-card-total">
                                            {item.total ? (
                                              <span className="total-amount">{formatNumber(item.total)} ₽</span>
                                            ) : '—'}
                                          </div>
                                        </div>
                                        {editingItemId === item.id && (
                                          <div className="estimate-card-actions">
                                            <button
                                              className="btn-save-item-mobile"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleSaveItem(item.id);
                                              }}
                                              title="Сохранить"
                                            >
                                              <i className="ri-check-line"></i>
                                              <span>Сохранить</span>
                                            </button>
                                            <button
                                              className="btn-cancel-item-mobile"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleCancelEditItem();
                                              }}
                                              title="Отменить"
                                            >
                                              <i className="ri-close-line"></i>
                                              <span>Отменить</span>
                                            </button>
                                          </div>
                                        )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </>
                            )}
                            
                            {/* Кнопка добавления элемента в секцию - только если есть элементы */}
                            {sectionItemsList.length > 0 && (
                              <div className="estimate-section-add-item">
                                <button
                                  className="btn-add-item-in-section"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCreateItem(section.id);
                                  }}
                                  title="Добавить пункт (элемент) в эту секцию"
                                >
                                  <i className="ri-add-line"></i>
                                  <span>Добавить пункт</span>
                                </button>
                              </div>
                            )}
                            
                            {/* Рекурсивно отображаем дочерние секции */}
                            {section.children && section.children.length > 0 && (
                              <div className="estimate-section-children-tree">
                                {section.children.map(child => renderSection(child, level + 1))}
                                
                                {/* Кнопка создания подсекции - после списка дочерних секций */}
                                <div className="estimate-section-add-child" style={{ marginLeft: `${(level + 1) * 16}px`, marginTop: '8px' }}>
                                  <button
                                    className="btn-add-item-in-section"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleCreateChildSection(section.id);
                                    }}
                                    title="Добавить подсекцию"
                                  >
                                    <i className="ri-folder-add-line"></i>
                                    <span>Добавить секцию</span>
                                  </button>
                                </div>
                              </div>
                            )}
                            
                            {/* Пустая секция: показываем две кнопки - создать подсекцию и создать пункт */}
                            {sectionItemsList.length === 0 && (!section.children || section.children.length === 0) && (
                              <div className="estimate-section-empty-tree">
                                <div className="tree-indent" style={{ width: `${(level + 1) * 16}px`, flexShrink: 0 }}>
                                  {level >= 0 && <div className="tree-line"></div>}
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'row', gap: '8px', flex: 1, padding: '8px 0' }}>
                                  <button
                                    className="btn-add-item-in-section"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleCreateChildSection(section.id);
                                    }}
                                    title="Создать подсекцию"
                                    style={{ flex: 1, justifyContent: 'center' }}
                                  >
                                    <i className="ri-folder-add-line"></i>
                                    <span>Создать секцию</span>
                                  </button>
                                  <button
                                    className="btn-add-item-in-section"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleCreateItem(section.id);
                                    }}
                                    title="Создать пункт (элемент)"
                                    style={{ flex: 1, justifyContent: 'center' }}
                                  >
                                    <i className="ri-file-add-line"></i>
                                    <span>Создать пункт</span>
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  };
                  
                  // Отображаем все корневые секции
                  return (
                    <>
                      {treeStructure.map(section => renderSection(section, 0))}
                      
                      {/* Кнопка создания корневой секции - после всех секций */}
                      {treeStructure.length > 0 && (
                        <div className="estimate-section-add-root" style={{ marginTop: '16px' }}>
                          <button
                            className="btn-add-item-in-section"
                            onClick={handleCreateSection}
                            title="Добавить новую секцию"
                          >
                            <i className="ri-folder-add-line"></i>
                            <span>Добавить секцию</span>
                          </button>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </>
          ) : structure.length === 0 && !loading && !loadingStructure ? (
            <div className="estimate-detail-empty">
              <i className="ri-inbox-line"></i>
              <span>В смете пока нет позиций</span>
              <div style={{ marginTop: '16px' }}>
                <button
                  className="btn-create-section-compact-empty"
                  onClick={handleCreateSection}
                  title="Создать первую секцию"
                >
                  <i className="ri-folder-add-line"></i>
                  <span>Создать секцию</span>
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* Нижнее меню действий для выделенных секций */}
      <ActionsMenuBar
        isOpen={showActionsMenuBar && sectionActions.length > 0}
        actions={sectionActions}
        onClose={() => {
          setShowActionsMenuBar(false);
          setSelectedSections(new Set());
        }}
        onCancel={() => {
          setSelectedSections(new Set());
        }}
      />

      {/* Нижнее меню действий для выделенных элементов */}
      <ActionsMenuBar
        isOpen={showItemActionsMenuBar && itemActions.length > 0}
        actions={itemActions}
        onClose={() => {
          setShowItemActionsMenuBar(false);
          setSelectedItems(new Set());
        }}
        onCancel={() => {
          setSelectedItems(new Set());
        }}
      />
    </BottomSheet>
  );
};

export default EstimateDetailModal;

